/**
 * PATH: packages/email-hub/src/feature/components/InboxSweepStartDialog.tsx
 * FILE_ID: EMAILHUB-COMPONENT-INBOX-SWEEP-START-001
 * PURPOSE: Preview-first "Clean up inbox" dialog for one mailbox.
 * WHY: Existing business mailboxes must never bulk-file without counts + confirm.
 * FLOW: preview → show plan → execute → 409 if already swept → banner owns undo.
 * RELATED: inboxSweepApi.ts, InboxSweepBanner.tsx, mailboxMenuItems.ts
 */
import { useEffect, useState } from 'react'
import { Loader2, Sparkles, X } from 'lucide-react'

import { useToast } from '../../_internal/components/Toast'
import {
  executeInboxSweep,
  previewInboxSweep,
  sweepErrorStatus,
  type InboxSweepPreviewPlan,
} from '../lib/inboxSweepApi'

export interface InboxSweepStartDialogProps {
  accountId: number
  mailboxLabel: string
  onClose: () => void
  /** Fires after a successful execute so the host can refetch + show banner. */
  onExecuted?: () => void
}

type Phase = 'loading' | 'preview' | 'executing' | 'error'

export function InboxSweepStartDialog({
  accountId,
  mailboxLabel,
  onClose,
  onExecuted,
}: InboxSweepStartDialogProps) {
  const { toast } = useToast()
  const [phase, setPhase] = useState<Phase>('loading')
  const [plan, setPlan] = useState<InboxSweepPreviewPlan | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [protectJobLinked, setProtectJobLinked] = useState(true)
  const keepCount = 50

  useEffect(() => {
    let cancelled = false
    setPhase('loading')
    setError(null)
    previewInboxSweep({
      account_id: accountId,
      keep_count: keepCount,
      protect_job_linked: protectJobLinked,
    })
      .then((p) => {
        if (cancelled) return
        setPlan(p)
        setPhase('preview')
      })
      .catch((e: Error) => {
        if (cancelled) return
        setError(e.message || 'Could not preview the clean-up.')
        setPhase('error')
      })
    return () => {
      cancelled = true
    }
  }, [accountId, protectJobLinked])

  const run = () => {
    setPhase('executing')
    setError(null)
    executeInboxSweep({
      account_id: accountId,
      keep_count: keepCount,
      protect_job_linked: protectJobLinked,
    })
      .then(() => {
        toast('Inbox clean-up started — older mail is being filed.', 'success')
        onExecuted?.()
        onClose()
      })
      .catch((e: unknown) => {
        const status = sweepErrorStatus(e)
        if (status === 409) {
          setError(
            'This mailbox already has a live clean-up. Undo it from the banner first, or wait for it to finish.',
          )
        } else {
          setError(e instanceof Error ? e.message : 'Clean-up failed.')
        }
        setPhase('preview')
      })
  }

  return (
    <div
      data-testid="inbox-sweep-start-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="Clean up inbox"
        className="w-full max-w-md rounded-lg border border-border bg-background shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <Sparkles className="h-4 w-4 text-primary" aria-hidden />
          <span className="text-sm font-medium">Clean up inbox</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            Keep the newest {keepCount} messages in{' '}
            <span className="font-medium text-foreground">{mailboxLabel}</span>, file
            older visible mail to Done / Junk. Nothing runs until you confirm.
          </p>

          {phase === 'loading' && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Counting messages…
            </div>
          )}

          {plan && phase !== 'loading' && (
            <dl
              data-testid="inbox-sweep-preview-counts"
              className="grid grid-cols-2 gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs"
            >
              <div>
                <dt className="text-muted-foreground">Visible</dt>
                <dd className="font-semibold">{plan.total_visible}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Keep</dt>
                <dd className="font-semibold">{plan.kept_count}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">File to Done</dt>
                <dd className="font-semibold">{plan.done_count}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">File to Junk</dt>
                <dd className="font-semibold">{plan.junk_count}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-muted-foreground">Protected (job-linked)</dt>
                <dd className="font-semibold">{plan.protected_count}</dd>
              </div>
            </dl>
          )}

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={protectJobLinked}
              onChange={(e) => setProtectJobLinked(e.target.checked)}
              data-testid="inbox-sweep-protect-jobs"
            />
            Protect job-linked emails
          </label>

          {error && (
            <p data-testid="inbox-sweep-error" className="text-xs text-destructive">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={run}
              disabled={phase === 'loading' || phase === 'executing' || !plan}
              data-testid="inbox-sweep-execute"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {phase === 'executing' && (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              )}
              {phase === 'executing' ? 'Cleaning…' : 'Clean up inbox'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
